/**
 * lib/permissions.ts — ÚNICA FUENTE DE VERDAD para permisos.
 */

import type { UserRole } from '@/types/core'
import { useAuthStore } from '@/stores/auth.store'

const PERMISSION_MAP = {
  'module:dashboard':       ['admin', 'supervisor', 'agente', 'agente_legal'],
  'module:marcados':        ['admin', 'supervisor', 'agente'],
  'module:cobros':          ['admin', 'supervisor', 'agente'],
  'module:pagos':           ['admin', 'supervisor', 'agente'],
  'module:impugnaciones':   ['admin', 'supervisor', 'agente'],
  'module:embargo':         ['admin', 'supervisor', 'agente'],
  'module:habeas':          ['admin', 'supervisor', 'agente'],
  'module:clientes':        ['admin', 'supervisor', 'agente', 'agente_legal'],
  'module:reportes':        ['admin', 'supervisor'],
  'module:usuarios':        ['admin'],
  'module:configuracion':   ['admin'],
  'module:campanas':        ['admin', 'supervisor'],
  'module:call-center':     ['admin', 'supervisor'],
  'module:judicial':        ['admin', 'supervisor'],

  'queue:view_all':         ['admin', 'supervisor'],
  'queue:view_assigned':    ['admin', 'supervisor', 'agente'],
  'queue:manage':           ['admin', 'supervisor'],

  'clients:view':           ['admin', 'supervisor', 'agente'],
  'clients:view_all':       ['admin', 'supervisor'],
  'clients:edit_status':    ['admin', 'supervisor', 'agente'],
  'clients:assign':         ['admin', 'supervisor'],
  'clients:bulk_assign':    ['admin'],
  'clients:delete':         ['admin'],

  'impugnaciones:view':     ['admin', 'supervisor', 'agente'],
  'impugnaciones:edit':     ['admin', 'supervisor'],
  'impugnaciones:assign':   ['admin', 'supervisor'],
  'impugnaciones:resolve':  ['admin'],

  'embargo:view':           ['admin', 'supervisor', 'agente'],
  'embargo:edit':           ['admin', 'supervisor'],
  'embargo:approve':        ['admin', 'supervisor'],

  'cobros:view':            ['admin', 'supervisor', 'agente'],
  'cobros:edit':            ['admin', 'supervisor', 'agente'],
  'cobros:escalate':        ['admin', 'supervisor'],

  'pagos:view':             ['admin', 'supervisor', 'agente'],
  'pagos:register':         ['admin', 'supervisor', 'agente'],
  'pagos:approve':          ['admin', 'supervisor'],
  'pagos:pay_commission':   ['admin'],

  'habeas:view':            ['admin', 'supervisor', 'agente'],
  'habeas:edit':            ['admin', 'supervisor'],
  'habeas:consentimiento':  ['admin', 'supervisor', 'agente'],

  'campaigns:view':         ['admin', 'supervisor', 'agente'],
  'campaigns:create':       ['admin'],
  'campaigns:edit':         ['admin'],
  'campaigns:assign':       ['admin', 'supervisor'],

  'cc:workspace':           ['admin', 'supervisor', 'agente'],
  'cc:supervision':         ['admin', 'supervisor'],
  'cc:force_logoff':        ['admin', 'supervisor'],
  'cc:remove_queue':        ['admin', 'supervisor'],

  'exports:clients':        ['admin', 'supervisor'],
  'exports:audit':          ['admin'],
  'exports:cobros':         ['admin', 'supervisor'],

  'users:view':             ['admin'],
  'users:create':           ['admin'],
  'users:edit':             ['admin'],
  'users:deactivate':       ['admin'],

  'audit:view_all':         ['admin', 'supervisor'],
  'audit:view_technical':   ['admin'],

  'ui:tech_status':         ['admin'],
  'ui:audit_panel':         ['admin', 'supervisor'],
  'ui:supervisor_banner':   ['admin', 'supervisor'],

  'legal:view':             ['admin', 'agente_legal'],
  'legal:embargos':         ['admin', 'agente_legal'],
  'legal:levantamientos':   ['admin', 'agente_legal'],
  'legal:import':           ['admin'],
  'legal:plantillas':       ['admin'],

  'judicial:view':          ['admin', 'supervisor'],
  'judicial:edit':          ['admin'],
} as const satisfies Record<string, readonly UserRole[]>

export type Permission = keyof typeof PERMISSION_MAP

export function can(role: UserRole, permission: Permission): boolean {
  return (PERMISSION_MAP[permission] as readonly string[]).includes(role)
}

export function canAny(role: UserRole, permissions: Permission[]): boolean {
  return permissions.some((p) => can(role, p))
}

export function canAll(role: UserRole, permissions: Permission[]): boolean {
  return permissions.every((p) => can(role, p))
}

export function canAccessModule(role: UserRole, moduleId: string): boolean {
  const key = `module:${moduleId}` as Permission
  if (!(key in PERMISSION_MAP)) return false
  return can(role, key)
}

export function getAccessibleModules(role: UserRole): string[] {
  return Object.keys(PERMISSION_MAP)
    .filter((k) => k.startsWith('module:'))
    .filter((k) => can(role, k as Permission))
    .map((k) => k.replace('module:', ''))
}

export function usePermissions() {
  const role = useAuthStore((s) => s.role)
  return {
    role,
    can:             (p: Permission) => can(role, p),
    canAny:          (ps: Permission[]) => canAny(role, ps),
    canAll:          (ps: Permission[]) => canAll(role, ps),
    canAccessModule: (id: string) => canAccessModule(role, id),
    isAdmin:         role === 'admin',
    isSupervisor:    role === 'supervisor',
    isAgente:        role === 'agente',
    isAtLeastSupervisor: role === 'admin' || role === 'supervisor',
    canExport:        can(role, 'exports:clients'),
    canViewAudit:     can(role, 'audit:view_all'),
    canManageUsers:   can(role, 'users:view'),
    canViewTechInfo:  can(role, 'ui:tech_status'),
    canAssign:        can(role, 'clients:assign'),
    canViewAllQueue:  can(role, 'queue:view_all'),
    canApprovEmbargo: can(role, 'embargo:approve'),
    showAgentBanner:  can(role, 'ui:supervisor_banner'),
    isAgenteLegal:    role === 'agente_legal',
    canAccessLegal:   role === 'admin' || role === 'agente_legal',
  }
}
